<template>
  <b-container fluid>
    <h1>Classroom Attainment</h1>
    <div v-if="loaded">
      <mio-filters label="Select country:" :options="classroomAttainmentData.filters" />
      <mio-output
        title="Corner coverage"
        :messages="classroomAttainmentData.messages"
        :tables="classroomAttainmentData.coverage"
        :method="classroomAttainmentData.coverageMethod"
      />
      <mio-output
        title="Average rate of corner adoption (days)"
        :messages="classroomAttainmentData.messages"
        :tables="classroomAttainmentData.rate"
        :method="classroomAttainmentData.rateMethod"
      />
    </div>
    <h2 v-else class="loading-placeholder">Loading...</h2>
  </b-container>
</template>

<script>
import { mapActions } from 'vuex';
import { set, get, validateRow } from '@/utils/MioHelpers';
import MioFilters from '@/components/MioFilters';
import MioOutput from '@/components/MioOutput';

export default {
  name: 'MioClassroomAttainment',
  components: {
    MioFilters,
    MioOutput,
  },
  data() {
    return {
      loaded: false,
    }
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions({
      GET_CLASSROOM_ATTAINMENT_SMALL: "mio/GET_CLASSROOM_ATTAINMENT_SMALL",
    }),
    async getData() {
      try {
        await this.GET_CLASSROOM_ATTAINMENT_SMALL({ fromDate: '2019-01-01' });

        this.loaded = true;
      } catch (err) {
        console.error(err);
      }
    }
  },
  computed: {
    classroomAttainmentData: {
      get() {
        const working = this.$store.state.mio.classroomAttainmentSmall,
          output = {
            coverageMethod: "Corners are tracked by processing all classroom observations in chronological order. Classrooms do not have their own unique ID in the database but the unique IDs of school and teacher are combined as an analogue. Once a corner appears for a classroom we increase our count for that type of corner and assume it remains active in that classroom thereafter, unless a subsequent observation contradicts that, in which case the coverage is reduced again accordingly.",
            rateMethod: "Rate of corner coverage is calculated by tracking the number of days between a classroom first being observed, and a corner being marked as true.",
            messages: [],
          }, cols = {
            human: {
              engaged_principal: 'engaged principal',
              greet: 'greet',
              good_attitude: 'good attitude',
            },
            PPI: {
              work_displayed: 'work displayed',
              arts: 'arts',
              music: 'music',
              dress_up_box: 'dress up box',
              nature_box: 'nature box',
              conservation: 'conservation',
              gardening: 'gardening',
              matching_games: 'matching games',
              puzzles: 'puzzles',
              reading_corner: 'reading corner',
              book_making: 'book making',
              play_shop: 'play shop',
            },
          };

        output.coverage = Object.keys(working.cumulativeData).sort().map(country => ({
          title: country,
          cols: { ...cols.PPI, average: 'average' },
          rows: Object.keys(working.cumulativeData[country]).sort().reverse().map(month => ({
            title: `${month}<br><small><em>${working.cumulativeData[country][month].classrooms} classrooms observed to date</em></small>`,
            subRows: ['percentage', 'number'].map(metric => ({
              class: (metric === 'number') ? 'small' : 'font-weight-bold',
              data: (metric === 'number')
                ? Object.keys(cols.PPI).map(col => working.cumulativeData[country][month][col] || '0')
                : Object.keys(cols.PPI).map(col => ((100 * (working.cumulativeData[country][month][col] || 0) / working.cumulativeData[country][month].classrooms).toFixed(0) + '%'))
                  .concat(`<span class="text-secondary">${((100 * Object.keys(cols.PPI).reduce((total, col) => total + working.cumulativeData[country][month][col], 0) / (Object.keys(cols.PPI).length * working.cumulativeData[country][month].classrooms)) || 0).toFixed(0)}%</span>`),
            })),
          })),
        }));

        output.rate = Object.keys(working.daysToAdopt).sort().map(country => ({
          title: country,
          cols: cols.PPI,
          rows: [{
            data: Object.keys(cols.PPI).map((ppi) => working.daysToAdopt[country][ppi]),
          }],
        }));

        // Add country filters
        output.filters = Object.keys(working.cumulativeData).sort();

        return output;
      },
    },
  },
};
</script>
